// @ts-nocheck
import { useEffect, useRef } from 'react';
import { conversationOrder } from '@/services/dialer/inboundSms';
import { smsDelivery } from '@/services/dialer/smsThread';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Loader2, Send, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { formatDate, formatDateTime, formatDurationClock } from '@/i18n/locale';
import { formatPhoneNumber } from '@/utils';

/**
 * L'onglet SMS de la fiche contact : la conversation, les modeles, et le
 * numero depuis lequel le message part.
 */

export default function ProspectSmsTab({
  prospect,
  smsHistory,
  smsTemplates,
  smsMessage,
  setSmsMessage,
  sendingSms,
  handleSendSms,
  numerosSms,
  numeroEnvoi,
  setNumeroEnvoi,
}) {
  const { t } = useTranslation(['admin', 'dialer', 'common']);

  const conversation = conversationOrder(smsHistory || []);

  // Ouvrir sur le dernier message, comme une messagerie : sans cela on
  // arrive en haut, sur le plus ancien, et il faut descendre pour lire ce
  // qui vient d'arriver.
  const filRef = useRef(null);
  useEffect(() => {
    const fil = filRef.current;
    if (fil) fil.scrollTop = fil.scrollHeight;
  }, [conversation.length]);

  return (
    <div className="flex flex-col h-full">
        <div className="flex items-center justify-between gap-3 mb-4">
            <h3 className="font-semibold text-foreground truncate">
                {formatPhoneNumber(prospect.prospect_phone)}
            </h3>
            {/* Depuis quel numero le message part. Un seul numero
                disponible : on l'affiche sans faire choisir. */}
            {numerosSms.length > 1 ? (
                <select
                    value={numeroEnvoi}
                    onChange={(e) => setNumeroEnvoi(e.target.value)}
                    aria-label={t('dialer:prospectPanel.sms.fromLabel')}
                    className="h-8 rounded-md border border-border bg-card px-2 text-xs text-foreground"
                >
                    {numerosSms.map((n) => (
                        <option key={n.phoneNumber} value={n.phoneNumber}>
                            {t('dialer:prospectPanel.sms.fromLabel')} {formatPhoneNumber(n.phoneNumber)}
                        </option>
                    ))}
                </select>
            ) : numeroEnvoi ? (
                <span className="text-xs text-muted-foreground shrink-0">
                    {t('dialer:prospectPanel.sms.fromLabel')} {formatPhoneNumber(numeroEnvoi)}
                </span>
            ) : null}
        </div>

        {/* La conversation, du plus ancien en haut au plus recent en bas,
            comme toute messagerie. Le serveur rend l'inverse, parce qu'il
            trie pour des listes. `vf-scroll-discreet` : on lit une
            conversation, on ne surveille pas une file. */}
        <div
            ref={filRef}
            className="flex-1 min-h-0 overflow-y-auto vf-scroll-discreet mb-4 space-y-3"
        >
            {conversation.length === 0 ? (
                <div className="text-center py-8">
                    <MessageSquare className="w-10 h-10 text-muted-foreground/40 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground/60">{t('admin:components.prospectDetail.noSms')}</p>
                </div>
            ) : (
                conversation.map((sms, idx) => (
                    <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`max-w-[80%] rounded-lg p-3 ${
                            sms.direction === 'outbound'
                                ? 'ml-auto bg-primary text-primary-foreground'
                                : 'mr-auto bg-muted text-foreground'
                        }`}
                    >
                        <p className="text-sm whitespace-pre-wrap">{sms.message}</p>
                        <p className={`text-xs mt-1 ${sms.direction === 'outbound' ? 'text-primary-foreground/70' : 'text-muted-foreground/60'}`}>
                            {formatDate(sms.timestamp, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            {smsDelivery(sms.status)
                                ? ` · ${t(`pages:sms.delivery.${smsDelivery(sms.status)}`)}`
                                : sms.status === 'sent' && ` · ${t('dialer:prospectPanel.sms.sent')}`}
                        </p>
                    </motion.div>
                ))
            )}
        </div>

        {/* Quick templates */}
        <div className="flex flex-wrap gap-2 mb-3">
            {smsTemplates.map((tpl, idx) => (
                <button
                    key={idx}
                    onClick={() => setSmsMessage(tpl.text)}
                    className="text-xs px-3 py-1.5 bg-muted hover:bg-accent rounded-full text-foreground/80 transition-colors"
                >
                    {tpl.label}
                </button>
            ))}
        </div>

        {/* Compose area */}
        <div className="border border-border rounded-lg p-3 surface-1">
            <Textarea
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                placeholder={t('admin:components.prospectDetail.writeSmsPlaceholder')}
                className="min-h-[80px] resize-none border-0 bg-transparent focus-visible:ring-0 p-0"
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        handleSendSms();
                    }
                }}
            />
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                <span className={`text-xs ${smsMessage.length > 160 ? 'text-orange-500 font-medium' : 'text-muted-foreground/60'}`}>
                    {smsMessage.length}/160
                    {smsMessage.length > 160 && t('admin:components.prospectDetail.smsSegmentCount', { count: Math.ceil(smsMessage.length / 153) })}
                </span>
                <Button
                    onClick={handleSendSms}
                    disabled={sendingSms || !smsMessage.trim()}
                    className="h-8 px-4 text-sm"
                >
                    {sendingSms ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <>
                            <Send className="w-3.5 h-3.5 mr-1.5" />
                            {t('admin:components.smsPanel.send')}
                        </>
                    )}
                </Button>
            </div>
        </div>
        <p className="text-xs text-muted-foreground/60 mt-2">{t('admin:components.prospectDetail.sendShortcut')}</p>
    </div>
  );
}
